/**
 * Handwritten Note Form Store
 *
 * Holds shared form state for the handwritten note flow: template choice,
 * selected template/design project id, message/closing/printed name, font, recipient contact id,
 * and return address. Used by the personalize page and step components.
 * Stepper is scroll-only (no step index in store that blocks navigation).
 *
 * Autosave: debounced PATCH (500ms for text fields including composed_message, 100ms for other
 * fields), mirroring personalizeForm.store. Wire body still uses project field names; the server
 * persists message/closing/printed_name/composed_message/contact/recipient/preview on the primary
 * `handwritten_note` and overlays them on GET/PATCH responses.
 *
 * Hero preview: child emits a Fabric `data:` URL; uploads merge into denormalized `preview_image[]`
 * on the primary note (`card_front` slot). Envelope face uploads merge the `envelope_face` slot.
 */

import {
    DEFAULT_HANDWRITING_PROJECT_FONT,
    HANDWRITING_PROJECT_FONT_OPTIONS,
    type HandwritingProjectFont,
} from '../constants/handwritingProjectFont'
import { useHandwrittenEnvelopeFacePreviewUpload } from '~~/layers/base/composables/useHandwrittenEnvelopeFacePreviewUpload'
import { useHandwrittenHeroPreviewUpload } from '~~/layers/base/composables/useHandwrittenHeroPreviewUpload'
import { useHandwritingProjectService } from '~~/layers/base/services/handwritingProject.service'
import { useShoppingSessionStore } from '~~/layers/base/stores/shoppingSession.store'
import type { HandwritingProjectRecord } from '~~/layers/base/types/handwritingProject'
import type { StationeryPreviewImage } from '~~/layers/base/types/stationeryPreviewImage'
import {
    applySessionDataUrlPreviewsToHandwritingProject,
    normalizeProjectPreviewImages,
} from '~~/layers/base/utils/handwritingProjectDenormalizedPreview'
import { isHandwritingProjectFont } from '~~/layers/base/utils/isHandwritingProjectFont'
import type { HandwrittenNoteFormState } from '~~/layers/base/types/handwrittenNote'
import { composeHandwrittenNoteMessage } from '~~/layers/base/utils/composeHandwrittenNoteMessage'
import type { FormError } from '~~/layers/base/utils/handwrittenNoteFormSchema'
import { debounce } from '~~/layers/base/utils/debounce'

export type TemplateChoice = 'preset' | 'customize' | 'my_handwriting'

/** Section config for form field sections (preview + one or more fields). Last section is review, outside this array. */
export type FormFieldType =
    | 'stationery'
    | 'message'
    | 'message-group'
    | 'font'
    | 'recipient'
    | 'return-address'

/** Field config for a single form field; type determines which component is rendered.
 * Aligns with design form (personalizeForm.store): required `id` for DOM, validation, and list keys. */
export interface HandwrittenNoteFormFieldConfig {
    type: FormFieldType
    /** Form field id (for DOM, validation/scroll-to-error, and list keys). */
    id: string
}

export interface FormFieldsetConfig {
    id: string
    title?: string
    subtitle?: string
    /** Fields to render in this fieldset (order preserved). */
    fields?: HandwrittenNoteFormFieldConfig[]
}

export interface FormFieldSectionConfig {
    id: string
    title: string
    /** Optional subtitle shown in the section header (on primary background). */
    subtitle?: string
    /** Optional fieldsets; each fieldset's content is rendered in a white block. */
    fieldsets?: FormFieldsetConfig[]
}

/** Step id and title for stepper and scroll; derived from formFieldSections + review section. */
export interface FormStepItem {
    id: string
    title: string
}

/**
 * Static section config for the send form. Exported so pre-form loading states can render the real
 * section hero (title, subtitle, step count) without instantiating this store or waiting on a fetch.
 */
export const HANDWRITTEN_NOTE_FORM_SECTIONS: FormFieldSectionConfig[] = [
    {
        id: 'template-message',
        title: 'Handwritten Message',
        subtitle: 'Send a genuine message written with a real pen',
        fieldsets: [
            {
                id: 'template-message-fieldset',
                fields: [
                    { type: 'stationery', id: 'stationery' },
                    { type: 'message-group', id: 'message-group' },
                    { type: 'font', id: 'font' },
                ],
            },
        ],
    },
    {
        id: 'recipient-return-address',
        title: 'Recipient & Addresses',
        subtitle: "Where it's going and where it returns",
        fieldsets: [
            {
                id: 'recipient-return-address-fieldset',
                fields: [
                    { type: 'recipient', id: 'recipient' },
                    { type: 'return-address', id: 'return-address' },
                ],
            },
        ],
    },
]

/** Final stepper step, appended after {@link HANDWRITTEN_NOTE_FORM_SECTIONS}. */
export const HANDWRITTEN_NOTE_REVIEW_STEP: FormStepItem = {
    id: 'review-send',
    title: 'Review & Send',
}

function defaultHandwrittenNoteFont(): HandwritingProjectFont {
    return DEFAULT_HANDWRITING_PROJECT_FONT
}

const fontOptions = computed(() => HANDWRITING_PROJECT_FONT_OPTIONS)

export const useHandwrittenNoteFormStore = defineStore('handwrittenNoteForm', () => {
    /** Placeholder text for the message field (body only; closing/name have their own fields). */
    const messageFieldPlaceholder = computed(
        () => `Hey Joe,\n\nSo great chatting with you last week...`
    )

    /** Placeholder for the optional closing line. */
    const closingFieldPlaceholder = computed(() => 'Best regards,')

    /** Placeholder for the optional printed name. */
    const printedNameFieldPlaceholder = computed(() => '- Michael')

    /** Minimum textarea rows so the full placeholder is visible without scrolling. Extra lines added for spacing. */
    const messageFieldMinRows = computed(() => messageFieldPlaceholder.value.split('\n').length + 4)

    /** Sections that have a preview and one or more fields (configured here; last section is review, outside the loop). */
    const formFieldSections = ref<FormFieldSectionConfig[]>(HANDWRITTEN_NOTE_FORM_SECTIONS)

    /** All steps for stepper and scroll: formFieldSections + review section. */
    const formSteps = computed<FormStepItem[]>(() => [
        ...formFieldSections.value,
        HANDWRITTEN_NOTE_REVIEW_STEP,
    ])

    /** 'preset' = use preset template; 'customize' = use designer/custom product. */
    const templateChoice = ref<TemplateChoice>('customize')

    /** Selected preset config template id (when templateChoice === 'preset'). */
    const selectedTemplateId = ref<string | null>(null)

    /** Selected handwriting project id or config template id (when templateChoice === 'customize'). */
    const selectedHandwritingProjectId = ref<string | null>(null)

    /** Message body text for the note. */
    const message = ref('')

    /** Optional closing line (e.g. "Best regards,"). */
    const closing = ref('')

    /** Optional printed name under the closing. */
    const printedName = ref('')

    /**
     * Concatenated print/preview string from message + closing + printedName.
     * Derived immediately for Fabric/fit; also included in the autosave PATCH body.
     */
    const composedMessage = computed(() =>
        composeHandwrittenNoteMessage(message.value, closing.value, printedName.value)
    )

    /** Selected font key; options are `HANDWRITING_PROJECT_FONT_OPTIONS` (Custom tile + presets). Defaults to first preset. */
    const font = ref<HandwritingProjectFont | null>(defaultHandwrittenNoteFont())

    /** Selected recipient contact id (single recipient). */
    const selectedContactId = ref<string | null>(null)

    /** Selected recipient address id (with selectedContactId forms composite recipient). */
    const selectedAddressId = ref<string | null>(null)

    /** Mirrored `handwriting_stationery_id` used by PATCH autosave and server hydrate. */
    const handwritingStationeryId = ref<string | null>(null)
    /** Mirrored `off_the_shelf_stationery_id` used by PATCH autosave and server hydrate. */
    const offTheShelfStationeryId = ref<string | null>(null)

    /** Selected return address id (`handwriting_project.return_address_id`). */
    const selectedReturnAddressId = ref<string | null>(null)

    /** Main-form validation errors (for UForm and field error display). Avoids prop drilling. */
    const formErrors = ref<FormError[]>([])

    /** Skip autosave while hydrating from API or during reset (mirrors personalizeForm `isResettingForm`). */
    const isResettingForm = ref(false)

    /** When false, autosave does not PATCH (e.g. guest / no session). Set from the personalize page. */
    const autosaveEnabled = ref(false)

    /**
     * Mirrors denormalized note `preview_image` during the session (merged on hero +
     * envelope uploads; re-hydrated from GET overlay).
     */
    const projectDenormalizedPreviews = ref<StationeryPreviewImage[]>([])

    /** Enable/disable autosave PATCH behavior from page-level auth/session context. */
    function setAutosaveEnabled(enabled: boolean) {
        autosaveEnabled.value = enabled
    }

    /** Store current validation errors so page and fields render the same error list. */
    function setFormErrors(errors: FormError[]) {
        formErrors.value = errors
    }

    /** Composite id for recipient field lookup: "contactId:addressId" when both set, else contact id only, else "". */
    const handwrittenRecipientValue = computed(() => {
        const cid = selectedContactId.value
        const aid = selectedAddressId.value
        if (cid && aid) return `${cid}:${aid}`
        return cid ?? ''
    })

    /** Single form state object for UForm :state and validation. Keys match FormField name and patch:field (camelCase). */
    const formState = computed<HandwrittenNoteFormState>(() => ({
        message: message.value,
        closing: closing.value,
        printedName: printedName.value,
        composedMessage: composedMessage.value,
        font: font.value ?? '',
        selectedTemplateId: selectedTemplateId.value ?? '',
        handwrittenRecipient: handwrittenRecipientValue.value,
        returnAddressId: selectedReturnAddressId.value ?? '',
    }))

    /** Set recipient from composite id "contactId:addressId" or contact id only. Used when patching handwrittenRecipient. */
    function setHandwrittenRecipient(value: string | null) {
        if (!value?.trim()) {
            selectedContactId.value = null
            selectedAddressId.value = null
            return
        }
        const parts = value.trim().split(':')
        if (parts.length >= 2) {
            selectedContactId.value = parts[0]! || null
            selectedAddressId.value = parts[1]! || null
        } else {
            selectedContactId.value = value.trim()
            selectedAddressId.value = null
        }
    }

    /**
     * Keeps `handwriting_stationery_id` / `off_the_shelf_stationery_id` aligned with template UI.
     * Pass the current user handwriting row id when `templateChoice === 'my_handwriting'` (from catalog `/me`).
     */
    function syncStationeryFkFromTemplateState(userHandwritingStationeryId: string | null) {
        const tc = templateChoice.value
        if (tc === 'preset' && selectedTemplateId.value) {
            handwritingStationeryId.value = null
            offTheShelfStationeryId.value = selectedTemplateId.value
        } else if (tc === 'my_handwriting') {
            offTheShelfStationeryId.value = null
            handwritingStationeryId.value = userHandwritingStationeryId
        } else {
            handwritingStationeryId.value = null
            offTheShelfStationeryId.value = null
        }
    }

    /** Clear selected return address id. */
    function resetReturnAddress() {
        selectedReturnAddressId.value = null
    }

    /**
     * Set when recipient/return fields change; cleared after a successful PATCH that then
     * calls `refreshCartAddresses` so ACCS gets DB addresses only after they persist.
     */
    let shouldRefreshCartAddressesAfterSave = false

    /** Persist empty text fields as null so PATCH matches create/clear semantics. */
    function emptyToNull(value: string): string | null {
        return value === '' ? null : value
    }

    /**
     * Send a PATCH autosave for the currently selected handwriting project.
     * This is the single write path used by both text/non-text debounced callbacks,
     * and it intentionally exits early when autosave is disabled or no project id exists.
     *
     * After a successful save that included recipient/return changes, re-runs prepare-cart
     * against the existing ACCS cart (`refreshCartAddresses`) so billing/shipping catch up.
     */
    async function patchHandwritingProjectToServer() {
        const projectId = selectedHandwritingProjectId.value
        if (!projectId || !autosaveEnabled.value) {
            return
        }
        const refreshCartAfterSave = shouldRefreshCartAddressesAfterSave
        const { doPatchHandwritingProject } = useHandwritingProjectService()
        const { error } = await doPatchHandwritingProject(projectId, {
            message: emptyToNull(message.value),
            closing: emptyToNull(closing.value),
            printed_name: emptyToNull(printedName.value),
            composed_message: emptyToNull(composedMessage.value),
            font: font.value ?? null,
            contact_id: selectedContactId.value,
            recipient_address_id: selectedAddressId.value,
            return_address_id: selectedReturnAddressId.value,
            handwriting_stationery_id: handwritingStationeryId.value,
            off_the_shelf_stationery_id: offTheShelfStationeryId.value,
        })
        if (error.value) {
            // eslint-disable-next-line no-console
            console.error('Handwriting autosave failed:', error.value)
            return
        }
        if (refreshCartAfterSave) {
            shouldRefreshCartAddressesAfterSave = false
            void useShoppingSessionStore().refreshCartAddresses()
        }
    }

    const { debouncedFn: debouncedSaveHandwritingText, cancel: cancelTextDebounce } = debounce(
        () => {
            void patchHandwritingProjectToServer()
        },
        500,
        'saveHandwritingProject'
    )

    const { debouncedFn: debouncedSaveHandwritingNonText, cancel: cancelNonTextDebounce } =
        debounce(
            () => {
                void patchHandwritingProjectToServer()
            },
            100,
            'saveHandwritingProject'
        )

    /**
     * Hero preview upload pipeline (debounce + throttle + user_image create/patch + preview_image sync)
     * lives in a composable so this store remains focused on form state + autosave shape.
     */
    const {
        onHeroPreviewPayload,
        cancelPendingHeroPreviewUploadWork,
        resetHeroPreviewUploadSession,
        heroPreviewLatest,
        userImageRequestInFlight: heroUserImageRequestInFlight,
    } = useHandwrittenHeroPreviewUpload({
        isResettingForm,
        autosaveEnabled,
        selectedHandwritingProjectId,
        projectDenormalizedPreviews,
    })

    const {
        onEnvelopeFacePreviewPayload,
        cancelPendingEnvelopeFacePreviewUploadWork,
        resetEnvelopeFacePreviewUploadSession,
        envelopeFacePreviewLatest,
        userImageRequestInFlight: envelopeFaceUserImageRequestInFlight,
    } = useHandwrittenEnvelopeFacePreviewUpload({
        isResettingForm,
        autosaveEnabled,
        selectedHandwritingProjectId,
        projectDenormalizedPreviews,
    })

    /**
     * True while any hero or envelope-face upload pipeline is mid-write:
     * `user_image` POST/PATCH **or** the downstream note `preview_image` PATCH.
     */
    const userImageRequestInFlight = computed(
        () => heroUserImageRequestInFlight.value || envelopeFaceUserImageRequestInFlight.value
    )

    /**
     * Resolve once preview upload pipelines are idle (card front + envelope face).
     * Used at checkout Pay / Send Note Now (TK-12688) — not on personalize Send.
     */
    function waitForPreviewUploadIdle(): Promise<void> {
        if (!userImageRequestInFlight.value) {
            return Promise.resolve()
        }
        return new Promise((resolve) => {
            const stop = watch(
                userImageRequestInFlight,
                (inFlight) => {
                    if (!inFlight) {
                        stop()
                        resolve()
                    }
                },
                { immediate: true }
            )
        })
    }

    /**
     * Overlay current form field values onto a cached project row (same fields as autosave PATCH /
     * `hydrateFromServer`). Used before Send navigates so checkout/success do not flash stale
     * server cache when debounced autosave has not flushed yet.
     */
    function withLatestFormFieldsForStoreCache(
        project: HandwritingProjectRecord
    ): HandwritingProjectRecord {
        return {
            ...project,
            message: emptyToNull(message.value),
            closing: emptyToNull(closing.value),
            printed_name: emptyToNull(printedName.value),
            composed_message: emptyToNull(composedMessage.value),
            font: font.value ?? null,
            contact_id: selectedContactId.value,
            recipient_address_id: selectedAddressId.value,
            return_address_id: selectedReturnAddressId.value,
            handwriting_stationery_id: handwritingStationeryId.value,
            off_the_shelf_stationery_id: offTheShelfStationeryId.value,
        }
    }

    /**
     * Clone a submitted project row with in-session Fabric `data:` previews overlaid on `preview_image`
     * so the success screen can render immediately without waiting on Cloudflare delivery URLs.
     */
    function withSessionDataUrlPreviewsForStoreCache(
        project: HandwritingProjectRecord
    ): HandwritingProjectRecord {
        return applySessionDataUrlPreviewsToHandwritingProject(project, {
            cardFront: heroPreviewLatest.value,
            envelopeFace: envelopeFacePreviewLatest.value,
        })
    }

    /** Cancel text, non-text, and preview-upload debounces before hydrate/reset so stale work does not run afterward. */
    function cancelHandwritingAutosaveDebounces() {
        cancelTextDebounce()
        cancelNonTextDebounce()
        cancelPendingHeroPreviewUploadWork()
        cancelPendingEnvelopeFacePreviewUploadWork()
    }

    /**
     * Hydrate editable store refs from a server handwriting-project row while autosave is paused.
     *
     * Sequence:
     * - cancel pending text/non-text debounced saves, then set `isResettingForm = true`
     * - write scalar refs (`message`, `closing`, `printedName`, `font`, recipient ids, stationery FK ids) from server values
     * - normalize `font` through `isHandwritingProjectFont` with fallback to default font
     * - derive template UI state from stationery FK precedence:
     *   `handwriting_stationery_id` => `my_handwriting`,
     *   else `off_the_shelf_stationery_id` => `preset`,
     *   else => `customize`
     * - clear `isResettingForm` on `nextTick` so reactive watchers skip intermediate writes
     *   during hydration but resume autosave immediately after the state settles
     */
    function hydrateFromServer(
        row: Pick<
            HandwritingProjectRecord,
            | 'message'
            | 'closing'
            | 'printed_name'
            | 'font'
            | 'contact_id'
            | 'recipient_address_id'
            | 'return_address_id'
            | 'handwriting_stationery_id'
            | 'off_the_shelf_stationery_id'
            | 'preview_image'
        >
    ) {
        cancelHandwritingAutosaveDebounces()
        resetHeroPreviewUploadSession()
        resetEnvelopeFacePreviewUploadSession()
        projectDenormalizedPreviews.value = normalizeProjectPreviewImages(
            row.preview_image as unknown
        )
        isResettingForm.value = true
        message.value = row.message ?? ''
        closing.value = row.closing ?? ''
        printedName.value = row.printed_name ?? ''
        font.value =
            row.font != null && isHandwritingProjectFont(row.font)
                ? row.font
                : defaultHandwrittenNoteFont()
        selectedContactId.value = row.contact_id
        selectedAddressId.value = row.recipient_address_id
        selectedReturnAddressId.value = row.return_address_id
        handwritingStationeryId.value = row.handwriting_stationery_id
        offTheShelfStationeryId.value = row.off_the_shelf_stationery_id
        // Preserve server intent with explicit precedence: custom user stationery beats preset off-the-shelf.
        if (row.handwriting_stationery_id) {
            templateChoice.value = 'my_handwriting'
            selectedTemplateId.value = null
        } else if (row.off_the_shelf_stationery_id) {
            templateChoice.value = 'preset'
            selectedTemplateId.value = row.off_the_shelf_stationery_id
        } else {
            templateChoice.value = 'customize'
            selectedTemplateId.value = null
        }
        // End reset guard after reactive writes flush so autosave watcher ignores hydrate-only mutations.
        nextTick(() => {
            isResettingForm.value = false
        })
    }

    const autosaveSnapshot = computed(() => ({
        message: message.value,
        closing: closing.value,
        printedName: printedName.value,
        composedMessage: composedMessage.value,
        font: font.value,
        templateChoice: templateChoice.value,
        selectedTemplateId: selectedTemplateId.value,
        selectedContactId: selectedContactId.value,
        selectedAddressId: selectedAddressId.value,
        selectedReturnAddressId: selectedReturnAddressId.value,
        handwritingStationeryId: handwritingStationeryId.value,
        offTheShelfStationeryId: offTheShelfStationeryId.value,
    }))

    /** Classify snapshot changes so text edits debounce slower than structural/non-text updates. */
    function detectAutosaveChangeType(
        newData: Record<string, unknown>,
        oldData: Record<string, unknown>
    ): 'text' | 'non-text' {
        if (
            newData.message !== oldData.message ||
            newData.closing !== oldData.closing ||
            newData.printedName !== oldData.printedName ||
            newData.composedMessage !== oldData.composedMessage
        ) {
            return 'text'
        }
        return 'non-text'
    }

    function didAddressFieldsChange(
        newData: Record<string, unknown>,
        oldData: Record<string, unknown>
    ): boolean {
        return (
            newData.selectedContactId !== oldData.selectedContactId ||
            newData.selectedAddressId !== oldData.selectedAddressId ||
            newData.selectedReturnAddressId !== oldData.selectedReturnAddressId
        )
    }

    /**
     * Autosave lifecycle watcher:
     * - skips initial run and reset/hydration windows
     * - requires enabled autosave + active project id
     * - routes message edits to slower debounce and all other changes to faster debounce
     * - marks recipient/return changes so a successful PATCH refreshes ACCS cart addresses
     */
    watch(
        autosaveSnapshot,
        (newData, oldData) => {
            if (
                oldData === undefined ||
                oldData === null ||
                (typeof oldData === 'object' && Object.keys(oldData as object).length === 0)
            ) {
                return
            }
            if (isResettingForm.value) {
                return
            }
            if (!autosaveEnabled.value || !selectedHandwritingProjectId.value) {
                return
            }
            const next = newData as Record<string, unknown>
            const prev = oldData as Record<string, unknown>
            if (didAddressFieldsChange(next, prev)) {
                shouldRefreshCartAddressesAfterSave = true
            }
            const changeType = detectAutosaveChangeType(next, prev)
            if (changeType === 'text') {
                debouncedSaveHandwritingText()
            } else {
                debouncedSaveHandwritingNonText()
            }
        },
        { deep: true }
    )

    /**
     * Reset all handwritten note form state to defaults.
     * Call when leaving the flow or starting a new note.
     */
    function resetHandwrittenNoteFormState() {
        cancelHandwritingAutosaveDebounces()
        resetHeroPreviewUploadSession()
        resetEnvelopeFacePreviewUploadSession()
        projectDenormalizedPreviews.value = []
        isResettingForm.value = true
        templateChoice.value = 'customize'
        selectedTemplateId.value = null
        selectedHandwritingProjectId.value = null
        message.value = ''
        closing.value = ''
        printedName.value = ''
        font.value = defaultHandwrittenNoteFont()
        selectedContactId.value = null
        selectedAddressId.value = null
        selectedReturnAddressId.value = null
        handwritingStationeryId.value = null
        offTheShelfStationeryId.value = null
        nextTick(() => {
            isResettingForm.value = false
        })
    }

    return {
        formFieldSections,
        formSteps,
        templateChoice,
        selectedTemplateId,
        selectedHandwritingProjectId,
        message,
        closing,
        printedName,
        composedMessage,
        messageFieldPlaceholder,
        messageFieldMinRows,
        closingFieldPlaceholder,
        printedNameFieldPlaceholder,
        font,
        selectedContactId,
        selectedAddressId,
        handwritingStationeryId,
        offTheShelfStationeryId,
        syncStationeryFkFromTemplateState,
        selectedReturnAddressId,
        formErrors,
        fontOptions,
        setFormErrors,
        formState,
        setHandwrittenRecipient,
        resetReturnAddress,
        resetHandwrittenNoteFormState,
        isResettingForm,
        autosaveEnabled,
        setAutosaveEnabled,
        hydrateFromServer,
        cancelHandwritingAutosaveDebounces,
        onHeroPreviewPayload,
        onEnvelopeFacePreviewPayload,
        userImageRequestInFlight,
        waitForPreviewUploadIdle,
        withLatestFormFieldsForStoreCache,
        withSessionDataUrlPreviewsForStoreCache,
    }
})
